import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { RefreshButton } from "@/components/RefreshButton";
import {
  Plus, BookOpen, Pencil, Trash2, MoreVertical, Search, Filter, ArrowUpDown, Layers, Eye, EyeOff, PenLine,
  Info, Users, PlaySquare, GraduationCap, Play, ListChecks, ClipboardCheck, MapPin, Copy, Monitor,
} from "lucide-react";
import { CourseFormDialog } from "@/components/CourseFormDialog";
import Combos from "@/pages/admin/Combos";

import { confirmar } from "@/components/ConfirmDialog";
import { SituacaoBadge, situacaoCurso } from "@/components/Presencial";
const COLS = [
  { key: "order", label: "Ordem" },
  { key: "status", label: "Status" },
  { key: "coordinator_name", label: "Coordenador" },
  { key: "title", label: "Curso" },
  { key: "category", label: "Categoria" },
];

export default function Courses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const [tab, setTab] = useState("cursos");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [fStatus, setFStatus] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [sort, setSort] = useState({ key: "order", dir: 1 });
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const load = () => { setSelected([]); return api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([])); };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [q, fStatus, fCategory, perPage]);

  const categories = useMemo(() => [...new Set((courses || []).map((c) => c.category).filter(Boolean))].sort(), [courses]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (courses || []).filter((c) =>
      (!term || c.title.toLowerCase().includes(term) || (c.coordinator_name || "").toLowerCase().includes(term)) &&
      (!fStatus || situacaoCurso(c) === fStatus) &&
      (!fCategory || c.category === fCategory));
    const val = (c) => sort.key === "status" ? ({ publicado: 0, realizado: 1, rascunho: 2 }[situacaoCurso(c)]) : sort.key === "order" ? (c.order || 0) : (c[sort.key] || "").toString().toLowerCase();
    return [...list].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort.dir);
  }, [courses, q, fStatus, fCategory, sort]);

  const pages = Math.max(1, Math.ceil(rows.length / perPage));
  const pageRows = rows.slice((page - 1) * perPage, page * perPage);
  const allOnPage = pageRows.length > 0 && pageRows.every((c) => selected.includes(c.id));

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = async (c) => {
    try { const r = await api.get(`/courses/${c.id}`); setEditing(r.data); setOpen(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!(await confirmar(`Excluir o curso "${c.title}"? Módulos e aulas dele também serão apagados.`))) return;
    try { await api.delete(`/courses/${c.id}`); toast.success("Curso excluído."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (ids, published) => {
    try {
      await api.post("/courses/bulk-status", { ids, published });
      toast.success(published ? "Publicado(s)." : "Movido(s) para rascunho.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  const toggleSel = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setSelected((p) => (allOnPage ? p.filter((id) => !pageRows.some((c) => c.id === id)) : [...new Set([...p, ...pageRows.map((c) => c.id)])]));

  if (tab === "combos") {
    return (
      <div className="space-y-5 animate-fade-up">
        <div><h1 className="font-display text-3xl font-bold text-slate-900">Todos os cursos</h1><p className="text-slate-500 mt-1">Crie e organize cursos, módulos e aulas.</p></div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="courses-tabs">
          <button onClick={() => setTab("cursos")} className="px-4 py-1.5 rounded-md text-sm font-medium text-slate-500">Cursos</button>
          <button onClick={() => setTab("combos")} className="px-4 py-1.5 rounded-md text-sm font-medium bg-white text-[#232D4F] shadow-sm" data-testid="tab-combos">Combos</button>
        </div>
        <Combos />
      </div>
    );
  }
  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="courses-tabs">
        <button onClick={() => setTab("cursos")} className="px-4 py-1.5 rounded-md text-sm font-medium bg-white text-[#232D4F] shadow-sm">Cursos</button>
        <button onClick={() => setTab("combos")} className="px-4 py-1.5 rounded-md text-sm font-medium text-slate-500" data-testid="tab-combos">Combos</button>
      </div>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Todos os cursos</h1>
          <p className="text-slate-500 mt-1">Crie e organize cursos, módulos e aulas.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <RefreshButton onClick={load} />
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" disabled={selected.length === 0} data-testid="btn-bulk-status">
                <PenLine className="h-4 w-4 mr-1.5" /> Alterar status{selected.length ? ` (${selected.length})` : ""}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setStatus(selected, true)}><Eye className="h-4 w-4 mr-2" /> Publicar</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setStatus(selected, false)}><EyeOff className="h-4 w-4 mr-2" /> Mover para rascunho</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button data-testid="btn-create-course" onClick={openNew} className="text-white font-semibold" style={{ background: "#1E9E2A" }}>
            <Plus className="h-4 w-4 mr-1.5" /> Novo
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-100">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar curso ou coordenador..." className="pl-9 h-10" data-testid="course-search" />
          </div>
          <Button variant={showFilters ? "default" : "outline"} onClick={() => setShowFilters((v) => !v)}
            className={showFilters ? "text-white" : ""} style={showFilters ? { background: "#232D4F" } : undefined}>
            <Filter className="h-4 w-4 mr-1.5" /> Filtros
          </Button>
          {showFilters && (
            <>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">Todos os status</option>
                <option value="publicado">Publicado</option>
                <option value="realizado">Realizado (presencial)</option>
                <option value="rascunho">Rascunho</option>
              </select>
              <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">Todas as categorias</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </>
          )}
        </div>

        {courses === null ? (
          <div className="p-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <BookOpen className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{courses.length ? "Nenhum curso com esses filtros." : "Nenhum curso ainda. Crie o primeiro!"}</p>
          </div>
        ) : (
          <div className="overflow-x-auto iep-scroll">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead className="w-10"><Checkbox checked={allOnPage} onCheckedChange={toggleAll} aria-label="Selecionar todos" /></TableHead>
                  {COLS.map((c) => (
                    <TableHead key={c.key} className={c.key === "coordinator_name" || c.key === "category" ? "hidden md:table-cell" : ""}>
                      <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 uppercase text-xs tracking-wide hover:text-slate-800">
                        {c.label} <ArrowUpDown className={`h-3 w-3 ${sort.key === c.key ? "text-[#232D4F]" : "text-slate-300"}`} />
                      </button>
                    </TableHead>
                  ))}
                  <TableHead className="text-right uppercase text-xs tracking-wide">Opções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((c) => (
                  <TableRow key={c.id} data-testid={`course-row-${c.id}`} className="cursor-pointer" onClick={() => navigate(`/admin/cursos/${c.id}`)}>
                    <TableCell onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleSel(c.id)} /></TableCell>
                    <TableCell className="text-slate-600 w-16">{c.order || "—"}</TableCell>
                    <TableCell>
                      <SituacaoBadge c={c} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600 whitespace-nowrap">{c.coordinator_name || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3 min-w-[240px]">
                        {c.banner
                          ? <img src={c.banner} alt="" loading="lazy" className="h-14 aspect-[3/2] rounded-lg object-cover border border-slate-200 shadow-sm shrink-0" />
                          : <div className="h-14 aspect-[3/2] rounded-lg grid place-items-center shrink-0" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}><BookOpen className="h-5 w-5 text-white/70" /></div>}
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800">{c.title}</div>
                          {c.modality === "presencial" ? (
                            <div className="text-xs text-[#B45309] flex items-center gap-1" data-testid={`course-presencial-${c.id}`}>
                              <MapPin className="h-3 w-3" /> {[c.city, c.state].filter(Boolean).join("/") || "Cidade não informada"}
                              {c.event_date ? ` · ${c.event_date.slice(8, 10)}/${c.event_date.slice(5, 7)}/${c.event_date.slice(0, 4)}` : ""}
                              {c.event_end && c.event_end !== c.event_date ? ` a ${c.event_end.slice(8, 10)}/${c.event_end.slice(5, 7)}/${c.event_end.slice(0, 4)}` : ""}
                            </div>
                          ) : <div className="text-xs text-slate-400 flex items-center gap-1"><Layers className="h-3 w-3" /> {c.module_count} módulos · {c.lesson_count} aulas</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {c.category ? (
                        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${c.modality === "presencial" ? "bg-orange-50 text-[#B45309] ring-orange-200" : "bg-[#232D4F]/[.06] text-[#232D4F] ring-[#232D4F]/15"}`}>
                          {c.modality === "presencial" ? <MapPin className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />}{c.category}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`course-options-${c.id}`} title="Opções"><MoreVertical className="h-4 w-4" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}#informacoes`)}><Info className="h-4 w-4 mr-2" /> Detalhes</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}#professores`)}><Users className="h-4 w-4 mr-2" /> Professores</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}#modulos`)}><Layers className="h-4 w-4 mr-2" /> Módulos</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}#aulas`)}><PlaySquare className="h-4 w-4 mr-2" /> Aulas</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}#questoes`)}><ListChecks className="h-4 w-4 mr-2" /> Banco de questões</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}#prova`)}><ClipboardCheck className="h-4 w-4 mr-2" /> Prova final</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}#matriculas`)}><GraduationCap className="h-4 w-4 mr-2" /> Matrículas</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => navigate(`/admin/cursos/${c.id}/ver`)}><Play className="h-4 w-4 mr-2" /> Ver curso</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => openEdit(c)} data-testid={`edit-course-${c.id}`}><Pencil className="h-4 w-4 mr-2" /> Editar dados do curso</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`duplicate-course-${c.id}`} onSelect={async () => {
                            if (!(await confirmar(`Duplicar "${c.title}"?\n\nA cópia é criada como RASCUNHO, com as mesmas informações, aulas e prova. Depois é só editar o que muda (nome, cidade, data...). O link de venda não é copiado.`))) return;
                            try { const r = await api.post(`/courses/${c.id}/duplicate`); toast.success("Curso duplicado. Edite as informações da cópia."); navigate(`/admin/cursos/${r.data.id}#informacoes`); }
                            catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                          }}><Copy className="h-4 w-4 mr-2" /> Duplicar curso</DropdownMenuItem>
                          {c.published
                            ? <DropdownMenuItem onSelect={() => setStatus([c.id], false)}><EyeOff className="h-4 w-4 mr-2" /> Mover para rascunho</DropdownMenuItem>
                            : <DropdownMenuItem onSelect={() => setStatus([c.id], true)}><Eye className="h-4 w-4 mr-2" /> Publicar</DropdownMenuItem>}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => remove(c)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
              <span className="mr-auto">{rows.length} curso(s)</span>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</Button>
              <span>{page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>›</Button>
              <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
                {[10, 20, 50].map((n) => <option key={n} value={n}>{n} / página</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <CourseFormDialog open={open} onOpenChange={setOpen} course={editing}
        onSaved={(c) => (editing ? load() : navigate(`/admin/cursos/${c.id}`))} />
    </div>
  );
}
